'use client';

import {
    cloneElement,
    createContext,
    Fragment,
    isValidElement,
    ReactElement,
    ReactNode,
    useContext,
    useEffect,
    useState,
} from 'react';

import { useSettingStore } from '@/_stores/settingStore';
import { Language } from '@/_types/language';
import locationData from '@_constants/location.json';
interface LanguageContextType {
    t: (key: string, params?: Record<string, string | number>) => string;
    tJSX: (key: string, params?: Record<string, string | number>) => ReactNode;
    changeLanguage: (langCode: Language) => void;
    currentLanguage: Language;
}

const defaultLanguage: Language = {
    id: 0,
    langCode: 'en',
    name: 'English',
    nativeName: 'English',
    img: '',
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
    const settingStore = useSettingStore();

    const [currentLanguage, setCurrentLanguage] = useState<Language>(defaultLanguage);

    useEffect(() => {
        try {
            const savedLanguage = JSON.parse(localStorage.getItem('preferredLanguage') || '{}');
            if (savedLanguage) {
                setCurrentLanguage(
                    settingStore
                        .languageGetter()
                        .find((language) => language.langCode === savedLanguage.langCode) ||
                        defaultLanguage
                );
            }
        } catch (error) {
            const savedLanguage = localStorage.getItem('preferredLanguage');
            const language = settingStore
                .languageGetter()
                .find((language) => language.langCode === savedLanguage);
            localStorage.setItem('preferredLanguage', JSON.stringify(language || defaultLanguage));
            setCurrentLanguage(language || defaultLanguage);
        }
    }, [settingStore]);

    // 기본 번역 함수
    const t = (key: string, params?: Record<string, string | number>) => {
        const keys = key.split('.');
        const value = keys.reduce(
            (obj, k) => obj?.[k],
            locationData[currentLanguage.langCode] as any
        );

        if (value === undefined) {
            console.warn(`Translation key not found: ${key}`);
            return key;
        }

        // ${0}, ${1} 등의 파라미터 치환
        if (params) {
            return value.replace(/\${(\d+)}/g, (_: any, index: number) => {
                const paramKey = Object.keys(params)[Number(index)];
                return params[paramKey]?.toString() || '';
            });
        }

        return value;
    };

    // HTML 태그를 지원하는 번역 함수
    const tJSX = (key: string, params?: Record<string, string | number | ReactNode>) => {
        const keys = key.split('.');
        const value = keys.reduce(
            (obj, k) => obj?.[k],
            locationData[currentLanguage.langCode] as any
        );

        if (value === undefined) {
            console.warn(`Translation key not found: ${key}`);
            return <>{key}</>;
        }

        // 텍스트를 ${n} 패턴으로 분할
        const parts = value.split(/(\${[0-9]+})/g);

        return (
            <>
                {parts.map((part: string, idx: number) => {
                    // ${n} 패턴인지 확인
                    const match = part.match(/\${([0-9]+)}/);
                    if (match) {
                        const paramIndex = Number(match[1]);
                        const paramKey = Object.keys(params || {})[paramIndex];
                        const paramValue = paramKey && params ? params[paramKey] : '';

                        // HTML 문자열이면 dangerouslySetInnerHTML 사용
                        if (typeof paramValue === 'string' && paramValue.includes('<')) {
                            return (
                                <span key={idx} dangerouslySetInnerHTML={{ __html: paramValue }} />
                            );
                        }

                        // JSX.Element이면 그대로 반환
                        if (isValidElement(paramValue)) {
                            return cloneElement(paramValue as ReactElement, {
                                key: idx,
                            });
                        }

                        // 일반 값이면 문자열로 변환
                        return <Fragment key={idx}>{String(paramValue)}</Fragment>;
                    }

                    // 일반 텍스트
                    return <Fragment key={idx}>{part}</Fragment>;
                })}
            </>
        );
    };

    // 언어 변경 함수
    const changeLanguage = async (langCode: Language) => {
        // 로컬 상태 업데이트
        setCurrentLanguage(langCode);
        if (typeof window !== 'undefined') {
            localStorage.setItem('preferredLanguage', JSON.stringify(langCode));
        }
    };

    if (typeof window === 'undefined') {
        return null;
    }

    return (
        <LanguageContext.Provider
            value={{
                t,
                tJSX,
                changeLanguage,
                currentLanguage: currentLanguage || defaultLanguage,
            }}
        >
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (context === undefined) {
        throw new Error('useHyundaiLanguage must be used within a LanguageProvider');
    }
    return context;
};
